import { waitForAnchor } from '@bookora/ui';

import { ANCHOR_GRACE_MS, ANCHOR_WAIT_MS } from './timing';

/**
 * Every step's anchor, awaited together — without letting one absent anchor
 * hold the tour back. Each waits up to `ANCHOR_WAIT_MS` (a page still
 * loading), but once ANY of them is on the page the page has rendered, and
 * the rest get only `ANCHOR_GRACE_MS` more: an empty strip that never grows
 * (a patient with nothing to warn about) used to delay the first step by the
 * full wait. `null` = that anchor is not there (the runner skips its step).
 *
 * `timeoutMs` overrides the per-anchor wait and `graceMs: null` switches the
 * grace off — a second, deliberate look (the shell tour's recheck) waits for
 * a late anchor rather than for the first one to show up.
 */
export async function waitForAnchors(
  ids: readonly string[],
  opts: {
    signal?: AbortSignal;
    root?: ParentNode;
    timeoutMs?: number;
    graceMs?: number | null;
  } = {},
): Promise<(HTMLElement | null)[]> {
  const timeoutMs = opts.timeoutMs ?? ANCHOR_WAIT_MS;
  const graceMs = opts.graceMs === undefined ? ANCHOR_GRACE_MS : opts.graceMs;
  const inner = new AbortController();
  const onAbort = (): void => inner.abort();
  opts.signal?.addEventListener('abort', onAbort);
  let grace: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.all(
      ids.map(async (id) => {
        const el = await waitForAnchor(id, {
          timeoutMs,
          signal: inner.signal,
          root: opts.root,
        });
        if (el && graceMs !== null && grace === null && !inner.signal.aborted) {
          grace = setTimeout(() => inner.abort(), graceMs);
        }
        return el;
      }),
    );
  } finally {
    if (grace !== null) clearTimeout(grace);
    opts.signal?.removeEventListener('abort', onAbort);
  }
}
